import { createFileRoute, Link } from "@tanstack/react-router";
import { Cookie, ArrowRight } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { openCookieSettings } from "@/components/storefront/cookie-consent";

const LAST_UPDATED = "Νοέμβριος 2026";

type CookieRow = { name: string; purpose: string; duration: string };

type CookieGroup = {
  emoji: string;
  title: string;
  badge: string;
  description: string;
  rows: CookieRow[];
};

const groups: CookieGroup[] = [
  {
    emoji: "🛡️",
    title: "Απαραίτητα cookies",
    badge: "Πάντα ενεργά",
    description:
      "Χρειάζονται για τη βασική λειτουργία του καταστήματος (σύνδεση, καλάθι, ασφάλεια). Χωρίς αυτά το site δεν λειτουργεί σωστά, γι' αυτό δεν απαιτούν συναίνεση.",
    rows: [
      { name: "Συνεδρία σύνδεσης (auth)", purpose: "Διατηρεί τη σύνδεσή σου στον λογαριασμό σου.", duration: "Συνεδρία / έως αποσύνδεση" },
      { name: "Καλάθι αγορών", purpose: "Θυμάται τα προϊόντα που έχεις προσθέσει.", duration: "Έως 1 έτος" },
      { name: "mp-cookie-consent", purpose: "Αποθηκεύει τις προτιμήσεις σου για τα cookies.", duration: "6 μήνες" },
    ],
  },
  {
    emoji: "⚙️",
    title: "Λειτουργικά cookies",
    badge: "Με συναίνεση",
    description:
      "Βελτιώνουν την εμπειρία σου θυμούμενα επιλογές και προτιμήσεις σου.",
    rows: [
      { name: "Είδες πρόσφατα", purpose: "Εμφανίζει προϊόντα που είδες πρόσφατα.", duration: "Έως 6 μήνες" },
      { name: "Προτιμήσεις προβολής", purpose: "Θυμάται ρυθμίσεις όπως το πλάτος του καλαθιού.", duration: "Έως 1 έτος" },
    ],
  },
  {
    emoji: "📊",
    title: "Στατιστικά & Marketing cookies",
    badge: "Με συναίνεση",
    description:
      "Μας βοηθούν να καταλάβουμε πώς χρησιμοποιείς το κατάστημα και να σου δείχνουμε σχετικό περιεχόμενο. Ενεργοποιούνται μόνο αν συναινέσεις.",
    rows: [
      { name: "Analytics", purpose: "Ανώνυμα στατιστικά επισκεψιμότητας & χρήσης.", duration: "Έως 2 έτη" },
      { name: "Marketing", purpose: "Μέτρηση αποτελεσματικότητας & σχετικές προτάσεις.", duration: "Έως 2 έτη" },
      { name: "__klarna_sdk_klarna-shopping-session", purpose: "Klarna (τρίτος πάροχος), τοπική αποθήκευση: ασφάλεια, διαθεσιμότητα υπηρεσίας και πρόληψη απάτης. Για τα ενημερωτικά μηνύματα Klarna ενεργοποιείται μόνο με συναίνεση στα Στατιστικά & Marketing.", duration: "48 ώρες" },
      { name: "__klarna_sdk_config", purpose: "Klarna (τρίτος πάροχος), αποθήκευση συνεδρίας: αξιοπιστία, ασφάλεια, ασφαλής πρόσβαση και επίλυση τεχνικών σφαλμάτων. Η Klarna μπορεί να επεξεργάζεται στοιχεία συσκευής, πρόγραμμα περιήγησης, διεύθυνση IP, ζώνη ώρας και γλώσσα.", duration: "Έως κλείσιμο του browser" },
    ],
  },
];

function CookiesPage() {
  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 py-10 sm:py-14">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 sm:p-12">
          <div className="pointer-events-none absolute -right-6 -top-8 text-8xl opacity-20 select-none">🍪</div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm font-medium text-primary">
            <Cookie className="h-4 w-4" />
            Τα cookies μας — χωρίς ζάχαρη, με διαφάνεια
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Πολιτική Cookies</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Τα cookies είναι μικρά αρχεία που μας βοηθούν να λειτουργεί το κατάστημα και να βελτιώνουμε
            την εμπειρία σου. Εδώ θα δεις ποια χρησιμοποιούμε και γιατί. 🐼
          </p>
          <p className="mt-3 text-xs text-muted-foreground">Τελευταία ενημέρωση: {LAST_UPDATED}</p>
        </div>

        {/* Groups */}
        <div className="mt-10 space-y-4">
          {groups.map((g, i) => (
            <Card key={i} className="overflow-hidden border-border">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl">
                    {g.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold tracking-tight">{g.title}</h2>
                      <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">
                        {g.badge}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{g.description}</p>
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full border-collapse text-left text-sm">
                        <thead>
                          <tr className="text-xs uppercase tracking-wide text-muted-foreground/70">
                            <th className="border-b border-border py-2 pr-4 font-semibold">Cookie</th>
                            <th className="border-b border-border py-2 pr-4 font-semibold">Σκοπός</th>
                            <th className="border-b border-border py-2 font-semibold">Διάρκεια</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r, k) => (
                            <tr key={k} className="align-top">
                              <td className="border-b border-border/60 py-2.5 pr-4 font-medium text-foreground">{r.name}</td>
                              <td className="border-b border-border/60 py-2.5 pr-4 text-muted-foreground">{r.purpose}</td>
                              <td className="border-b border-border/60 py-2.5 text-muted-foreground whitespace-nowrap">{r.duration}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 rounded-3xl border border-border bg-muted/40 p-6 text-center sm:p-8">
          <p className="text-2xl">🍪</p>
          <h2 className="mt-2 text-xl font-semibold">Άλλαξες γνώμη;</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Μπορείς να αλλάξεις τις προτιμήσεις σου για τα cookies όποτε θέλεις.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Button onClick={openCookieSettings}>Ρυθμίσεις Cookies</Button>
            <Button asChild variant="outline">
              <Link to="/privacy">
                Πολιτική Απορρήτου
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/cookies" },
      { title: "Πολιτική Cookies — MrPanda.gr" },
      {
        name: "description",
        content: "Ποια cookies χρησιμοποιεί το MrPanda.gr, για ποιον σκοπό, και πώς να διαχειριστείς τις προτιμήσεις σου.",
      },
      { property: "og:title", content: "Πολιτική Cookies — MrPanda.gr" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      {
        property: "og:description",
        content: "Διαφάνεια στα cookies του MrPanda.gr — διάβασε ποια χρησιμοποιούμε και γιατί.",
      },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/cookies" }],
  }),
  component: CookiesPage,
});
